/**
 * API helpers for the voicemail feature.
 *
 * All calls go through the shared VRS API client for base URL, auth, and error handling.
 */
import { z } from 'zod';
import { apiClient, unwrapApiResponse } from '../shared/api-client';
import { usesServerRecorder } from './capture';
import { recordsOnDevice } from './deviceRecorder';
const recordingAcknowledgmentSchema = z.object({ success: z.literal(true) });
const publishedGreetingSchema = z.object({ greeting: z.object({
        id: z.string().min(1), status: z.literal('available'),
        durationSeconds: z.number().finite().nonnegative().nullable().optional(),
        playbackUrl: z.string().min(1).optional(), thumbnailUrl: z.string().min(1).optional()
    }) });
function confirmRecordingMutation(value) {
    const result = recordingAcknowledgmentSchema.safeParse(value);
    if (!result.success)
        throw new Error('The recording action could not be confirmed. Try again.');
    return result.data;
}
function voicemailPath(path) {
    return `/api/voicemail${path}`;
}
/**
 * Fetch the voicemail inbox.
 */
export async function fetchVoicemailInbox(limit, offset) {
    return unwrapApiResponse(await apiClient.get(voicemailPath(`/inbox?limit=${limit}&offset=${offset}`)));
}
/**
 * Fetch a single voicemail message with presigned playback URL.
 */
export async function fetchVoicemailMessage(messageId) {
    return unwrapApiResponse(await apiClient.get(voicemailPath(`/messages/${messageId}`)));
}
/**
 * Delete a voicemail message.
 */
export async function deleteVoicemailMessage(messageId) {
    return unwrapApiResponse(await apiClient.del(voicemailPath(`/messages/${messageId}`)));
}
/**
 * Mark a message as seen.
 */
export async function markMessageSeen(messageId) {
    return unwrapApiResponse(await apiClient.post(voicemailPath(`/messages/${messageId}/seen`)));
}
/**
 * Get the unread voicemail count.
 */
export async function fetchUnreadCount() {
    const data = unwrapApiResponse(await apiClient.get(voicemailPath('/unread-count')));
    return data.count;
}
/**
 * Start a voicemail recording session.
 */
/** The server refused because no recorder (Jibri) is connected — not a transient glitch to reload around. */
export const VIDEOMAIL_RECORDER_UNAVAILABLE = 'VIDEOMAIL_RECORDER_UNAVAILABLE';
export function isRecorderUnavailableError(error) {
    return error instanceof Error && error.name === VIDEOMAIL_RECORDER_UNAVAILABLE;
}
/**
 * Tells the server it need not have a recorder standing by, but only when
 * that is true. A client that does need one says nothing, so its request is
 * exactly what it always was.
 *
 * @returns {object} The claim to merge into a start request.
 */
function localCaptureClaim() {
    return usesServerRecorder() && !recordsOnDevice() ? {} : { localCapture: true };
}
async function startRecordingSession(path, body, prefix) {
    const request = new AbortController();
    const deadline = setTimeout(() => request.abort(), 30000);
    try {
        const result = await apiClient.post(voicemailPath(path), body, { retryAuth: false, signal: request.signal });
        if (result.code === VIDEOMAIL_RECORDER_UNAVAILABLE) {
            const refused = new Error(result.error || 'VideoMail recording is not available right now.');
            refused.name = VIDEOMAIL_RECORDER_UNAVAILABLE;
            throw refused;
        }
        const response = unwrapApiResponse(result);
        const parsed = z.object({
            messageId: z.string().regex(/^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i),
            roomName: z.string(), maxDurationSeconds: z.number().int().positive().safe()
        }).safeParse(response);
        if (!parsed.success || parsed.data.roomName !== `${prefix}${parsed.data.messageId}`) {
            throw new Error('VideoMail could not be confirmed. Check VideoMail before starting another recording.');
        }
        // The same answer that chose the start body chooses the screen.
        return { ...parsed.data, capture: body.localCapture && recordsOnDevice() ? 'device' : 'room' };
    }
    finally {
        clearTimeout(deadline);
    }
}
export async function startVoicemailRecording(target) {
    return startRecordingSession('/start', { ...target, ...localCaptureClaim() }, 'voicemail-');
}
/**
 * Cancel an active recording.
 */
export async function cancelVoicemailRecording(messageId) {
    return confirmRecordingMutation(unwrapApiResponse(await apiClient.post(voicemailPath(`/cancel/${messageId}`))));
}
/**
 * Send a finalized draft recording to the recipient inbox.
 */
export async function sendVoicemailRecording(messageId) {
    return confirmRecordingMutation(unwrapApiResponse(await apiClient.post(voicemailPath(`/send/${messageId}`))));
}
const greetingSchema = z.object({
    id: z.string().min(1), status: z.enum(['recording', 'draft', 'available', 'failed']),
    durationSeconds: z.number().finite().nonnegative().nullable().optional(),
    duration_seconds: z.number().finite().nonnegative().nullable().optional(),
    playbackUrl: z.string().optional(), thumbnailUrl: z.string().optional()
});
function checkedGreetingResponse(value, statuses, expectedId) {
    const parsed = z.object({ greeting: greetingSchema.nullable() }).safeParse(value);
    if (!parsed.success || expectedId && parsed.data.greeting?.id !== expectedId
        || parsed.data.greeting && !statuses.includes(parsed.data.greeting.status)) {
        throw new Error('Your VideoMail greeting could not be loaded. Try again.');
    }
    const row = parsed.data.greeting;
    if (!row)
        return { greeting: null };
    for (const link of [row.playbackUrl, row.thumbnailUrl]) {
        if (!link)
            continue;
        const url = new URL(link);
        if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password)
            throw new Error('The greeting video could not be loaded.');
    }
    return { greeting: { id: row.id, status: row.status,
            durationSeconds: row.durationSeconds ?? row.duration_seconds, playbackUrl: row.playbackUrl || undefined,
            thumbnailUrl: row.thumbnailUrl || undefined } };
}
async function readGreeting(path, statuses, expectedId) {
    const request = new AbortController();
    const deadline = setTimeout(() => request.abort(), 30000);
    try {
        return checkedGreetingResponse(unwrapApiResponse(await apiClient.get(voicemailPath(path), { signal: request.signal })), statuses, expectedId);
    }
    finally {
        clearTimeout(deadline);
    }
}
export async function getVideoMailGreeting() {
    return readGreeting('/greeting', ['available']);
}
export async function getPendingVideoMailGreeting() {
    return readGreeting('/greeting/pending', ['recording', 'draft']);
}
export async function startVideoMailGreeting() {
    return startRecordingSession('/greeting/start', localCaptureClaim(), 'videomail-greeting-');
}
/**
 * Uploads a take the browser recorded itself.
 *
 * @param {object} take - The finished recording and where it belongs.
 * @returns {Promise<unknown>} The server's acknowledgement.
 */
export async function uploadVideoMailRecording({ blob, contentType, durationSeconds, greetingId, messageId }) {
    const id = greetingId || messageId;
    if (!id) {
        throw new Error('A recording upload needs a greeting or message to belong to.');
    }
    return unwrapApiResponse(await postVideoMailRecording({ blob, contentType, durationSeconds, greetingId, messageId }));
}
/**
 * Sends a recorded take once and returns the server's answer with its HTTP
 * status, so a caller that retries can tell a lost connection from a refusal.
 *
 * @param {object} take - The finished recording and where it belongs.
 * @returns {Promise<object>} The API response, never thrown for HTTP errors.
 */
export async function postVideoMailRecording({ blob, contentType, durationSeconds, greetingId, messageId, timeoutMs = 120000 }) {
    const path = greetingId
        ? `/greeting/${encodeURIComponent(greetingId)}/recording`
        : `/recording/${encodeURIComponent(String(messageId))}`;
    const request = new AbortController();
    // A few tens of megabytes over a slow uplink: generous, but bounded so a
    // dead connection does not leave the review screen waiting forever.
    const deadline = setTimeout(() => request.abort(), timeoutMs);
    try {
        // upright=1: the take's rotation flag is correct. iOS builds before
        // patches/react-native-vision-camera+4.7.3.patch do not send it, and the
        // server corrects their takes (vrs-server/routes/voicemail.js).
        return await apiClient.post(`${voicemailPath(path)}?durationSeconds=${encodeURIComponent(String(durationSeconds))}&upright=1`, blob, { contentType, signal: request.signal });
    }
    finally {
        clearTimeout(deadline);
    }
}
export async function getVideoMailGreetingDraft(greetingId) {
    return readGreeting(`/greeting/drafts/${encodeURIComponent(greetingId)}`, ['draft', 'available'], greetingId);
}
async function mutateGreeting(greetingId, action) {
    const request = new AbortController();
    const deadline = setTimeout(() => request.abort(), 30000);
    try {
        return unwrapApiResponse(await apiClient.post(voicemailPath(`/greeting/${encodeURIComponent(greetingId)}/${action}`), {}, { signal: request.signal, retryAuth: false }));
    }
    finally {
        clearTimeout(deadline);
    }
}
export async function publishVideoMailGreeting(greetingId) {
    const response = await mutateGreeting(greetingId, 'publish');
    const confirmed = publishedGreetingSchema.safeParse(response);
    if (!confirmed.success || confirmed.data.greeting.id !== greetingId) {
        throw new Error('Greeting publication could not be confirmed. Try again.');
    }
    return confirmed.data;
}
export async function cancelVideoMailGreeting(greetingId) {
    return confirmRecordingMutation(await mutateGreeting(greetingId, 'cancel'));
}
